﻿import React, { useState, useEffect } from "react";
import { Box, TextField, InputAdornment } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepGridWarning,
  ShepPagination,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import AddEditCustomField from "./AddEditCustomField";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useDispatch } from "react-redux";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { setAllCustomFields } from "@/features/maintenance/maintenanceSlice";
import { SearchIcon } from "@/ImageRepo";

const ConnectionCustomFieldSummary = ({}) => {
  const dispatch = useDispatch();

  const [addCustomFieldOpen, setAddCustomFieldOpen] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  // search flter
  const [searchFilter, setSearchFilter] = useState("");

  const handleTextChange = (event) => {
    setSearchFilter(event.target.value);
  };

  const filteredFields = customFields.filter((field) => {
    if (!searchFilter) return true; // If search is empty, show all
    return field.CustomFieldCode?.toLowerCase().includes(
      searchFilter.toLowerCase(),
    );
  });

  useEffect(() => {
    getConnectionCustomFields();
  }, []);

  const columns = [
    {
      field: "CustomFieldCode",
      headerName: "Field Code ",
      width: 300,
    },
    {
      field: "CustomFieldName",
      headerName: "Field Name",
      width: 300,
    },
    {
      field: "CustomFieldDescription",
      headerName: "Field Description",
      flex: 1,
    },
    {
      field: "threedotMenu",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          showInMenu
          onClick={() => {
            setIsEditing(true);
            setRecordToEdit(params.row);
            setAddCustomFieldOpen(true);
          }}
        />,
      ],
    },
  ];

  const validateDelete = () => {
    if (selectedRowIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          message: "No records selected to delete",
        }),
      );
      return;
    }

    const warningMessage = `Are you sure you want to delete the selected record${
      selectedRowIds.length > 1 ? "s" : ""
    }?`;

    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "Delete Record",
        message: warningMessage,
        buttonLabel: "Delete",
        onButtonClick: () => handleDelete(),
        buttonLabel2: "Cancel",
      }),
    );
  };

  const handleDelete = () => {
    axios
      .delete(`/connectioncustomfield/custom-fields`, {
        data: selectedRowIds,
      })
      .then((response) => {
        if (response.data.inUse) {
          dispatch(
            createAlert({
              status: "Warning",
              alertType: "single",
              message: response.data.message,
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: response.data.message,
            }),
          );
        }
        getConnectionCustomFields();
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  const actions = [
    {
      label: "Add",
      onClick: () => {
        setIsEditing(false);
        setRecordToEdit(null);
        setAddCustomFieldOpen(true);
      },
    },
    {
      label: "Delete",
      onClick: () => validateDelete(),
      variant: "danger",
    },
  ];

  const getConnectionCustomFields = () => {
    axios
      .get("/connectioncustomfield/custom-fields", {})
      .then((response) => {
        if (response.data) {
          setCustomFields(response.data);
          dispatch(
            setAllCustomFields({
              allCustomFields: response.data,
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response.data,
          }),
        );
      });
  };

  return (
    <>
      <Box sx={{ display: "block", flexDirection: "column", width: "100%" }}>
        <Box
          className="content-box"
          sx={{ display: "flex", flexDirection: "row" }}
        >
          <Box sx={{ width: "100%" }}>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: "25px",
                paddingBottom: "25px",
              }}
            >
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        style={{ color: "var(--text-icon-regular)" }}
                      />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by Field Code"
                value={searchFilter}
                onChange={handleTextChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                }}
                sx={{
                  width: "450px",
                  marginBottom: "0px",
                  "&:hover fieldset": {
                    backgroundColor: "#ffffff",
                  },
                  "&.MuiFormControl-root .MuiInputBase-root": {
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    color: "#000000",
                  },
                }}
              />

              <ShepHorizontalMenu actions={actions} />
            </div>
            <AddEditCustomField
              open={addCustomFieldOpen}
              onClose={() => {
                setAddCustomFieldOpen(false);
                setIsEditing(false);
                setRecordToEdit(null);
              }}
              fetchCustomFields={getConnectionCustomFields}
              isEditing={isEditing}
              initialData={recordToEdit}
            />

            <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
              {filteredFields.length > 0 ? (
                <ShepDataGridPro
                  isDataLoaded={true}
                  rows={filteredFields}
                  columns={columns}
                  columnHeaderHeight={78}
                  rowHeight={100}
                  whiteHeader={true}
                  checkboxSelection={true}
                  disableMultipleRowSelection={false}
                  disableColumnMenu={true}
                  disableColumnFilter={true}
                  disableColumnReorder={true}
                  hideFooterSelectedRowCount={true}
                  pagination
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 15, page: 0 },
                    },
                  }}
                  slots={{
                    baseCheckbox: ShepCheckbox,
                    pagination: ShepPagination,
                  }}
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRowIds(newRowSelectionModel);
                  }}
                  getRowId={(row) => row.ConnectionCustomFieldId}
                  sx={{
                    color: "var(--text-primary)",
                    fontSize: "var(--font-sm)",
                    fontFamily: "var(--font-family)",
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    backgroundColor: "var(--default-light)",
                    border: "none",
                    "& .MuiDataGrid:hover": {
                      backgroundColor: "inherit",
                    },
                    "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                      opacity: 0,
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "var(--tertiary-13)",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "var(--primary-pale)",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                      {
                        outline: "none",
                      },
                    "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                      {
                        opacity: 1,
                      },
                    "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                      opacity: 1,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "var(--font-md)",
                      fontWeight: "var(--font-bold)",
                      borderLeft: "none",
                    },
                    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                      {
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                        backgroundColor: "var(--primary-pale)",
                        borderBottom: "none",
                        borderLeft: "none",
                      },
                  }}
                />
              ) : (
                <ShepGridWarning
                  title="No custom fields to display."
                  message="Please add a new custom field or adjust your filters."
                  sx={{ marginTop: "25px" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ConnectionCustomFieldSummary;
